import React, { useEffect, useMemo, useState } from 'react';
import { Drawer, Select, Tooltip } from 'antd';
import {
  CheckOutlined,
  DownOutlined,
  GlobalOutlined,
  RightOutlined,
} from '@ant-design/icons';
import './index.less';
import { useSearchParams } from 'react-router-dom';
import {
  ActionCreatorWithPayload,
  AnyAction,
  Dispatch,
} from '@reduxjs/toolkit';
const { Option } = Select;

interface Props {
  languagesArr: {
    code: string;
    dir: 'ltr' | 'rtl';
    name: string;
  }[];
  defaultLang: string | null;
  dispatch?: Dispatch<AnyAction>;
  changeLocale: (lang: string) => void;
  withoutBorders?: boolean;
  smallView?: boolean;
  wrapperClassName?: string;
  globeTooltip?: string;
  // Menu drawer triggers, both opening the language bottom sheet:
  //   'row'  settings-list row  (🌐 label · current language ›)
  //   'chip' compact pill       (🌐 EN ▾)
  // On phones the default globe opens the same sheet; desktop keeps the Select.
  variant?: 'default' | 'row' | 'chip';
  rowLabel?: string;
}

const SiteLang: React.FC<Props> = ({
  languagesArr,
  defaultLang,
  dispatch,
  changeLocale,
  withoutBorders,
  smallView = false,
  wrapperClassName,
  globeTooltip,
  variant = 'default',
  rowLabel,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [selectInputValue, setSelectInputValue] = useState(
    defaultLang ?? 'he_IL'
  );

  useEffect(() => {
    // Phone size = width ≤ 767px, the breakpoint B2C and BO share.
    const mq = window.matchMedia('(max-width: 767px)');
    setIsMobile(mq.matches);
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const handleLangChange = async (lang: string) => {
    changeLocale(lang);
    localStorage.setItem('lang', lang);
    if (searchParams.get('lang')) {
      searchParams.set('lang', lang);
      setSearchParams(searchParams);
    }
    setSelectInputValue(lang);
    setSheetOpen(false);
  };

  const filteredLangs = useMemo(() => {
    return languagesArr.filter(
      (lang) => !!lang?.dir && !!lang?.code && !!lang?.name
    );
  }, [languagesArr]);

  const currentName =
    filteredLangs.find((item) => item.code === selectInputValue)?.name ??
    selectInputValue;

  // Native language picker: bottom sheet with a checkmark list. Each option
  // is written in its own language and script direction.
  const sheet = (
    <Drawer
      open={sheetOpen}
      onClose={() => setSheetOpen(false)}
      placement="bottom"
      height="auto"
      closable={false}
      className="site-lang-sheet"
      destroyOnClose
    >
      <div className="site-lang-sheet__head">
        <GlobalOutlined />
        <span>{rowLabel || globeTooltip || ''}</span>
      </div>
      <div className="site-lang-sheet__list" role="radiogroup">
        {filteredLangs.map((item) => {
          const active = selectInputValue === item.code;
          return (
            <button
              key={item.code}
              type="button"
              role="radio"
              aria-checked={active}
              className={`site-lang-sheet__option${active ? ' is-active' : ''}`}
              onClick={() => handleLangChange(item.code)}
            >
              <span className="site-lang-sheet__code">
                {item.code.split('_')[0]?.toUpperCase()}
              </span>
              <span className="site-lang-sheet__name" dir={item.dir}>
                {item.name}
              </span>
              {active && <CheckOutlined className="site-lang-sheet__check" />}
            </button>
          );
        })}
      </div>
    </Drawer>
  );

  if (variant === 'chip') {
    return (
      <>
        <button
          type="button"
          className={`site-lang-chip ${wrapperClassName || ''}`}
          onClick={() => setSheetOpen(true)}
          aria-label={`${rowLabel || globeTooltip || 'Language'}: ${currentName}`}
        >
          <GlobalOutlined />
          <span>{selectInputValue?.split('_')[0]?.toUpperCase()}</span>
          <DownOutlined className="site-lang-chip__caret" />
        </button>
        {sheet}
      </>
    );
  }

  if (variant === 'row') {
    return (
      <>
        <button
          type="button"
          className={`site-lang-row ${wrapperClassName || ''}`}
          onClick={() => setSheetOpen(true)}
        >
          <span className="site-lang-row__icon">
            <GlobalOutlined />
          </span>
          <span className="site-lang-row__label">{rowLabel || globeTooltip}</span>
          <span className="site-lang-row__value">{currentName}</span>
          <RightOutlined className="site-lang-row__chevron" />
        </button>
        {sheet}
      </>
    );
  }

  if (isMobile) {
    return (
      <>
        <Tooltip title={globeTooltip}>
          <button
            type="button"
            className={`site-lang mobile-globe ${smallView ? 'smallView' : ''} ${wrapperClassName || ''}`}
            onClick={() => setSheetOpen(true)}
            aria-label={globeTooltip || 'Language'}
          >
            <GlobalOutlined className={'language-selector-globe'} />
          </button>
        </Tooltip>
        {sheet}
      </>
    );
  }

  return (
    <div
      className={`site-lang ${smallView ? 'smallView' : ''} ${
        wrapperClassName || ''
      }`}
      onClick={() => setIsDropdownOpen((prev) => !prev)}
    >
      <Tooltip title={globeTooltip}>
        <GlobalOutlined className={'language-selector-globe'} />
      </Tooltip>
      {smallView && (
        <span className="lang-code">{selectInputValue?.split('_')[1]}</span>
      )}
      <Select
        className={`language-selector ${smallView ? 'smallView' : ''}`}
        value={selectInputValue}
        onChange={(value) => {
          handleLangChange(value);
        }}
        open={isDropdownOpen}
        onBlur={() => setIsDropdownOpen(false)}
        bordered={!withoutBorders}
      >
        {filteredLangs &&
          filteredLangs.map((item) => {
            if (!item?.code) {
              return null;
            }
            return (
              <Option key={`${item?.code}-${item?.name}`} value={item?.code}>
                {item?.name}
              </Option>
            );
          })}
      </Select>
    </div>
  );
};

export default SiteLang;
